import { DOCUMENT } from '@angular/common';
import {
	Directive,
	ElementRef,
	EventEmitter,
	HostBinding,
	inject,
	Input,
	NgZone,
	OnInit,
	Output,
} from '@angular/core';
import { fromEvent, merge, Observable, Subject } from 'rxjs';
import { filter, map, skipWhile, switchMap, takeUntil, tap } from 'rxjs/operators';

import { MovementEndKey, MovementEvent } from './type';
import { DomUtils } from './utils';

@Directive({
	selector: '[reachSlider]',
	host: {
		class: 'reach-slider-cdk',
	},
	standalone: true,
})
export class ReachSliderDirective implements OnInit {
	private _canSwipe = true;

	@Input()
	public set reachSlider(canSwipe: boolean) {
		if (`${canSwipe}` === `true` || `${canSwipe}` === `false`) {
			const cleanValue = `${canSwipe}` === `true`;

			if (!cleanValue) {
				this.swipeManuallyCancelled$$.next();
			}

			this._canSwipe = cleanValue;
		}
	}

	public get reachSlider(): boolean {
		return this._canSwipe;
	}

	@Input()
	public pixelsToStartSlide = 10;

	@Input()
	public allowScrollFrom: string[] = [];

	@Input()
	public currentIndex = 0;

	@Input()
	public indexCount = 10;

	@HostBinding('style.--__extraLateralX.px')
	@Input()
	public extraLateralPixels = 50;

	protected _moving = false;

	public get moving(): boolean {
		return this._moving;
	}

	public get nativeElement(): HTMLElement {
		return this.elementRef.nativeElement;
	}

	@Output()
	public movementStart = new EventEmitter<MovementEvent>();

	@Output()
	public movement = new EventEmitter<MovementEvent>();

	@Output()
	public movementEnd = new EventEmitter<MovementEndKey>();

	protected milisecondsToFilterMovement = 100;
	protected lastMovements: MovementEvent[] = [];

	protected animating = false;

	// https://newbedev.com/cancel-click-event-in-the-mouseup-event-handler
	protected captureClick = this.captureClickBase.bind(this);

	private swipeManuallyCancelled$$ = new Subject<void>();

	protected readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
	protected readonly ngZone = inject(NgZone);
	protected readonly document = inject(DOCUMENT);

	protected domUtils = new DomUtils(this.document);

	ngOnInit(): void {
		this.domUtils.elementsToAllow = [this.elementRef.nativeElement];

		this.onMovement$<MouseEvent>(
			'mousedown',
			'mousemove',
			(ev) => ({ x: ev.clientX, y: ev.clientY }),
			'mouseup'
		).subscribe((e) => {
			this.ngZone.run(() => {
				this.handleMovement(e);
			});
		});

		this.onMovement$<TouchEvent>(
			'touchstart',
			'touchmove',
			(ev) => ({ x: ev.touches.item(0)?.clientX ?? 0, y: ev.touches.item(0)?.clientY ?? 0 }),
			'touchend'
		).subscribe((e) => {
			this.ngZone.run(() => {
				this.handleMovement(e);
			});
		});
	}

	protected onMovement$<T extends Event>(
		startEvent: string,
		moveEvent: string,
		coordsFn: (e: T) => { x: number; y: number },
		stopEvent: string
	): Observable<MovementEvent> {
		return fromEvent(this.elementRef.nativeElement, startEvent).pipe(
			filter(() => this.reachSlider && !this.animating),
			map((downEvent) => downEvent as T),
			tap(() => {
				this.lastMovements = [];
			}),
			switchMap((downEvent) => {
				return fromEvent(this.elementRef.nativeElement, moveEvent).pipe(
					map((moveEvent) => moveEvent as T),
					map((moveEvent) => {
						return this.mouseToMovement(downEvent, moveEvent, coordsFn);
					}),
					map((movement) => {
						return this.movementWithBouncing(movement);
					}),
					skipWhile((movement) => {
						return this.invalidMovementInitiation(movement);
					}),
					takeUntil(this.stopListeningToMovement$(stopEvent)),
					tap({
						complete: () => {
							this.ngZone.run(() => {
								if (this.moving) {
									window.addEventListener('click', this.captureClick, true);

									setTimeout(() => {
										window.removeEventListener(
											'click',
											this.captureClick,
											true
										);
									}, 100);
								}

								this.movementFinished();
								this.domUtils.enableScroll();
							});
						},
					})
				);
			})
		);
	}

	protected stopListeningToMovement$(stopEvent: string): Observable<void> {
		return merge(
			fromEvent(this.document, stopEvent),
			fromEvent(this.elementRef.nativeElement, 'scroll', { capture: true }).pipe(
				filter((e) => e.target !== this.elementRef.nativeElement),
				tap(() => {
					this.lastMovements = [];
				}),
				takeUntil(this.movementStart)
			),
			this.swipeManuallyCancelled$$.pipe(
				tap(() => {
					this.lastMovements = [];
				})
			)
		).pipe(map(() => {}));
	}

	protected mouseToMovement<T extends Event>(
		downEvent: T,
		moveEvent: T,
		coordsFn: (e: T) => { x: number; y: number }
	): MovementEvent {
		const firstPoint = coordsFn(downEvent);
		const firstPointX = firstPoint.x;
		const firstPointY = firstPoint.y;

		const currentPoint = coordsFn(moveEvent);
		const currentPointX = currentPoint.x;
		const currentPointY = currentPoint.y;

		const distanceX = firstPointX - currentPointX;

		const box = this.nativeElement.getBoundingClientRect();
		const width = box.width;

		const sign = Math.sign(distanceX);
		const rawPercentage = Math.abs(distanceX) / width;
		const percentageX = sign * rawPercentage;

		return {
			firstPointX,
			firstPointY,
			currentPointX,
			currentPointY,
			distanceX,
			percentageX,
			timestamp: performance.now(),
		};
	}

	protected movementWithBouncing(movement: MovementEvent): MovementEvent {
		const inFirstAndGoingPrev = this.currentIndex === 0 && movement.distanceX < 0;
		const inLastAndGoingNext =
			this.currentIndex === this.indexCount - 1 && movement.distanceX > 0;

		const rawDivisor =
			this.nativeElement.getBoundingClientRect().width / this.extraLateralPixels;
		const divisor = Number.isFinite(rawDivisor) && rawDivisor !== 0 ? rawDivisor : 1;

		if (inFirstAndGoingPrev || inLastAndGoingNext) {
			return {
				...movement,
				percentageX: movement.percentageX / divisor,
			};
		}

		return movement;
	}

	protected invalidMovementInitiation(movement: MovementEvent): boolean {
		const invalidDistance = Math.abs(movement.distanceX) < this.pixelsToStartSlide;
		return invalidDistance;
	}

	protected filterLastMovements(timestamp: number): void {
		const lastIdx = this.lastMovements.length - 1;
		this.lastMovements = this.lastMovements.filter(
			(m, idx) =>
				idx === lastIdx || timestamp - m.timestamp < this.milisecondsToFilterMovement
		);
	}

	protected handleMovement(event: MovementEvent): void {
		if (!this._moving) {
			this.movementStart.emit(event);
			this.domUtils.disableScroll();
		}

		this._moving = true;

		this.lastMovements.push(event);
		this.filterLastMovements(event.timestamp);

		this.movement.emit(event);
	}

	protected async movementFinished(): Promise<MovementEndKey> {
		this.filterLastMovements(performance.now());

		/**
		 * Now the first items are the last movements.
		 * We want to keep the last ones with the same direction.
		 */
		const movements = this.lastMovements.reverse().filter((m, idx, arr) => {
			if (idx === 0) {
				return true;
			}

			return Math.sign(arr[idx - 1].distanceX) === Math.sign(m.distanceX);
		});

		let movementResult = MovementEndKey.SWIPED_NEUTRAL;

		const lastPercent = movements[0]?.percentageX || 0;

		const swipeLeft = lastPercent <= -0.5;
		const swipeRight = lastPercent > 0.5;

		if (movements.length > 1) {
			movementResult =
				movements[0].distanceX < 0
					? MovementEndKey.SWIPED_LEFT_TO_RIGHT
					: movements[0].distanceX > 0
						? MovementEndKey.SWIPED_RIGHT_TO_LEFT
						: MovementEndKey.SWIPED_NEUTRAL;
		} else if (swipeLeft) {
			movementResult = MovementEndKey.SWIPED_LEFT_TO_RIGHT;
		} else if (swipeRight) {
			movementResult = MovementEndKey.SWIPED_RIGHT_TO_LEFT;
		}

		this._moving = false;
		this.movementEnd.emit(movementResult);

		return movementResult;
	}

	protected captureClickBase(event: Event): void {
		event.stopPropagation();
		window.removeEventListener('click', this.captureClick, true);
	}
}
